import { KeyboardEvent, memo } from 'react';
import { WelcomeOffersList } from './welcomesOfferList.tsx';
import useWelcomeOffers from '../../api/hooks/useWelcomeOffers.tsx';
import { PreferredWo } from '../../cms';
import Skeleton from '../../design-system/skeleton.tsx';
import { useFlowContext } from '../../hooks/useFlowContext.tsx';
import useFlowNavigation from '../../hooks/useFlowNavigation.tsx';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TWelcomeOfferField } from '../../types/types';
import { TEST_ID } from '../../utils/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';

const WelcomeOfferSelection = ({ name }: TWelcomeOfferField) => {
  const { config } = useFlowContext();
  const { setFormData, formData, metadata } = useRegistrationStore();
  const marketInfo = useMarketInfo();
  const { handleNext } = useFlowNavigation(marketInfo, config);

  const { market } = marketInfo;

  const browserWindow = window as Window & { fabric?: { persistedState?: { preferredWelcomeOffer?: string | null } } };
  const rawPreferredVertical = browserWindow.fabric?.persistedState?.preferredWelcomeOffer || '';

  const preferredVerticalSelection: PreferredWo = (() => {
    const preferredVertical = String(rawPreferredVertical).toLowerCase();
    if (preferredVertical.includes('live') && preferredVertical.includes('casino')) return 'live_casino';
    if (preferredVertical.includes('sport')) return 'sports';
    if (preferredVertical.includes('casino')) return 'casino';

    return '';
  })();

  const { offers, loading, error } = useWelcomeOffers(preferredVerticalSelection);

  const handleSelect = (offerId: string) => {
    const updatedData = { ...formData, welcomeOffer: offerId };
    setTimeout(
      () => localStorage.setItem(`data_${market}`, JSON.stringify(updatedData)),
      0,
    );
    localStorage.setItem(`data_${market}`, JSON.stringify(updatedData));
    setFormData({ welcomeOffer: offerId });
    handleNext({ redirectTo: '1' });
    GtmEvents.handleWelcomeOfferEvent(offerId, metadata?.currentStep);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.key === 'Enter' && handleSelect(name);
  };

  if (error) {
    throw new Error(error?.message);
  }

  if (
    !name ||
    loading ||
    !offers?.[0]?.thumbnailImage
  ) {
    return (
      <div className="flex flex-col gap-xs items-center">
        <Skeleton className="w-[60%] h-[120px] md:h-[150px]" />
        <Skeleton className="w-full h-[350px]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <div
        className="font-midnight-st font-bold text-white text-[32px] tablet:text-5xl 
        text-center uppercase max-w-[285px] tablet:max-w-[430px] self-center leading-8"
        dangerouslySetInnerHTML={{
          __html:
            offers?.[0].registrationTitle ?? offers?.[1].registrationTitle,
        }}
        data-testid={TEST_ID.TEXT}
      />
      <WelcomeOffersList
        handleOnKeyDown={handleOnKeyDown}
        handleSelect={handleSelect}
        welcomeOffers={offers}
      />
    </div>
  );
};

const Memoized = memo(WelcomeOfferSelection);
Memoized.displayName = 'WelcomeOffer';

export default Memoized;
